import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { allReportedBookings, changeReportedBookingsStatus } from '../../slice/reportedBookingsSlice'

const STATUS_OPTIONS = ['in_review', 'resolved', 'rejected', 'pending']

const STATUS_BADGE = {
    in_review: { label: 'In Review', color: '#fd7e14', bg: '#fff3e0' },
    resolved:  { label: 'Resolved',  color: '#198754', bg: '#e8f5e9' },
    rejected:  { label: 'Rejected',  color: '#dc3545', bg: '#fdecea' },
    pending:   { label: 'Pending',   color: '#6c757d', bg: '#f1f3f5' },
}

const ReportedBookingList = () => {
    const dispatch = useDispatch()
    const { reportedBookingList } = useSelector(state => state.reportedBooking)
    const navigate = useNavigate()

    const { page, setPage, limit, setLimit } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allReportedBookings({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    const fetchList = () => {
        dispatch(allReportedBookings({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined,
        }))
    }

    const handleView = (reportId) => {
        navigate('/reported-bookings/view', { state: { reportId } })
    }

    const handleChangeStatus = async (reportId, newStatus, adminNotes) => {
        try {
            const response = await dispatch(changeReportedBookingsStatus({
                id: reportId,
                status: newStatus,
                ...(adminNotes !== undefined && { admin_notes: adminNotes }),
            }))

            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message || 'Status updated successfully',
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
            fetchList()
        } catch (error) {
            console.error('Error updating report status:', error)
        }
    }

    const handleStatusSelect = async (row, newStatus) => {
        if (newStatus === row.status) return

        const badge = STATUS_BADGE[newStatus]

        // Prompt for optional admin notes when resolving or rejecting
        if (newStatus === 'resolved' || newStatus === 'rejected') {
            const { value: adminNotes, isConfirmed } = await Swal.fire({
                title: `Mark as ${badge.label}?`,
                input: 'textarea',
                inputLabel: 'Admin Notes (optional)',
                inputPlaceholder: 'Add a note about this decision...',
                inputAttributes: { rows: 3 },
                showCancelButton: true,
                confirmButtonColor: newStatus === 'resolved' ? '#198754' : '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: `Yes, mark as ${badge.label}`,
                cancelButtonText: 'Cancel',
                reverseButtons: true,
            })

            if (!isConfirmed) return
            await handleChangeStatus(row.id, newStatus, adminNotes || undefined)
        } else {
            await handleChangeStatus(row.id, newStatus)
        }
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((reportedBookingList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Report ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Booking ID',
            selector: row => row.booking?.booking_id || 'NA',
            sortable: true,
            minWidth: '180px',
        },
        {
            name: 'Reported By',
            minWidth: '180px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src={row.reported_by?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="reporter"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#212529' }}>
                            {`${row.reported_by?.first_name || ''} ${row.reported_by?.last_name || ''}`.trim() || 'NA'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            {row.reported_by_user_type
                                ? row.reported_by_user_type.charAt(0).toUpperCase() + row.reported_by_user_type.slice(1)
                                : 'NA'}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'Tutor',
            minWidth: '180px',
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                        src={row.booking?.tutor?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                        alt="tutor"
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                        }}
                        style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div style={{ fontSize: '13px', color: '#212529' }}>
                        {`${row.booking?.tutor?.first_name || ''} ${row.booking?.tutor?.last_name || ''}`.trim() || 'NA'}
                    </div>
                </div>
            ),
        },
        {
            name: 'Reason',
            minWidth: '220px',
            cell: row => (
                <div
                    title={row.reason}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px', fontSize: '13px' }}
                >
                    {row.reason || 'NA'}
                </div>
            ),
        },
        {
            name: 'Reported On',
            selector: row =>
                row.reported_on ? moment(row.reported_on).local().format('DD MMM YYYY, hh:mm A') : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Report Status',
            minWidth: '160px',
            center: true,
            cell: row => {
                const canEdit = hasPermission('reported_bookings', 'edit') && row.is_deleted !== 1
                const badge = STATUS_BADGE[row.status] || STATUS_BADGE['pending']

                // Read-only badge for users without edit permission or deleted records
                if (!canEdit) {
                    return (
                        <span style={{
                            display: 'inline-block',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: badge.color,
                            backgroundColor: badge.bg,
                            whiteSpace: 'nowrap',
                        }}>
                            {badge.label}
                        </span>
                    )
                }

                return (
                    <select
                        value={row.status || 'pending'}
                        onChange={(e) => handleStatusSelect(row, e.target.value)}
                        style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: badge.color,
                            backgroundColor: badge.bg,
                            border: `1px solid ${badge.color}55`,
                            borderRadius: '12px',
                            padding: '3px 22px 3px 10px',
                            cursor: 'pointer',
                            outline: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='${encodeURIComponent(badge.color)}'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 7px center',
                        }}
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>
                                {STATUS_BADGE[opt].label}
                            </option>
                        ))}
                    </select>
                )
            },
        },
        {
            name: 'Action',
            width: '80px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('reported_bookings', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: { style: { backgroundColor: '#ffffff', borderRadius: '8px' } },
        headRow: { style: { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6', minHeight: '48px' } },
        headCells: { style: { fontSize: '13px', fontWeight: '600', color: '#495057', paddingLeft: '12px', paddingRight: '12px' } },
        cells: { style: { fontSize: '13px', color: '#212529', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '12px', paddingRight: '12px' } },
        rows: {
            style: { minHeight: '60px', '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' } },
            stripedStyle: { backgroundColor: '#ffffff' },
        },
        pagination: { style: { borderTop: '1px solid #dee2e6', minHeight: '56px', fontSize: '13px' } },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Reported Bookings</h4>
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '16px' }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by booking ID, reporter name, or reason..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{ right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
                                style={{ height: '40px', fontSize: '14px', border: '1px solid #dee2e6', borderRadius: '6px' }}
                            >
                                <option value="all">All</option>
                                <option value="in_review">In Review</option>
                                <option value="resolved">Resolved</option>
                                <option value="rejected">Rejected</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={reportedBookingList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={reportedBookingList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={reportedBookingList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => { setLimit(newLimit); setPage(1) }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={reportedBookingList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-inbox" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No reports found matching your search' : 'No reported bookings found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Reported bookings will appear here'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default ReportedBookingList